import {
  localSyncQueue,
  type PendingSyncOperation,
  type SyncOperation,
} from './repositories';

export interface SyncAdapter {
  apply(operation: {
    entity: string;
    entity_id: string;
    operation: SyncOperation;
    payload: unknown;
  }): Promise<void>;
}

export interface SyncRunResult {
  processed: number;
  failed: number;
  remaining: number;
  errors: string[];
}

function describeSyncError(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (error && typeof error === 'object') {
    const structured = error as { code?: string; message?: string; details?: string; hint?: string };
    const parts = [structured.code, structured.message, structured.details, structured.hint].filter(Boolean);
    if (parts.length > 0) return parts.join(' - ');
  }
  return 'Unknown sync error';
}

export async function processSyncQueue(adapter: SyncAdapter, shouldProcess?: (operation: PendingSyncOperation) => boolean): Promise<SyncRunResult> {
  const pending = (await localSyncQueue.list()).filter((operation) => shouldProcess ? shouldProcess(operation) : true);
  let processed = 0;
  let failed = 0;
  const errors: string[] = [];

  for (const operation of pending) {
    try {
      await adapter.apply(operation);
      await localSyncQueue.remove(operation.id);
      processed += 1;
    } catch (error) {
      const message = describeSyncError(error);
      await localSyncQueue.update(operation.id, {
        attempts: operation.attempts + 1,
        last_error: message,
      });
      failed += 1;
      errors.push(`${operation.entity} ${operation.operation}: ${message}`);
    }
  }

  return {
    processed,
    failed,
    remaining: (await localSyncQueue.list()).length,
    errors,
  };
}

export async function getSyncQueue(): Promise<PendingSyncOperation[]> {
  return localSyncQueue.list();
}
